import React, { useMemo, useState } from "react";
import styled from "styled-components";
import * as Icon from "react-feather";
import { useHistory } from "react-router-dom";
import routes from "routes";
import { fetchClientsByIntermediary } from "services/summarySrv";

import TableComponent from "components/widgets/table";
import { useQuery } from "react-query";

const Page = styled.div`
    padding: 32px;
    background: #f8fafc;
    min-height: 100vh;
`;

const Header = styled.div`
    margin-bottom: 32px;
`;

const TitleSection = styled.div`
    margin-bottom: 24px;
`;

const Title = styled.h1`
    margin: 0 0 8px 0;
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 400;
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const MetricCard = styled.div`
    background: #ffffff;
    padding: 20px 24px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        border-color: #cbd5e1;
    }
`;

const MetricLabel = styled.div`
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const MetricValue = styled.div`
    font-size: 32px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
`;

const ControlsBar = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
`;

const SearchSection = styled.form`
    display: flex;
    gap: 12px;
    align-items: stretch;
    flex: 1;
    min-width: 300px;
`;

const Select = styled.select`
    padding: 10px 16px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #0f172a;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;

    &:hover { border-color: #cbd5e1; }
    &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`;

const SearchInputWrapper = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:focus-within {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    svg {
        position: absolute;
        left: 14px;
        pointer-events: none;
    }
`;

const SearchInput = styled.input`
    border: none;
    outline: none;
    padding: 10px 16px 10px 44px;
    font-size: 14px;
    color: #0f172a;
    width: 100%;
    background: transparent;

    &::placeholder { color: #94a3b8; }
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;
    white-space: nowrap;
    outline: none;

    ${(p) => p.variant === "primary" ? `
        background: #3b82f6;
        color: #ffffff;
        &:hover {
            background: #2563eb;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        &:active { transform: translateY(1px); }
    ` : `
        background: #ffffff;
        color: #64748b;
        border: 1px solid #e2e8f0;
        &:hover {
            background: #f8fafc;
            border-color: #cbd5e1;
        }
    `}

    &:focus { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;

const ContentCard = styled.div`
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
`;

const EmptyState = styled.div`
    padding: 80px 24px;
    text-align: center;
`;

const EmptyIcon = styled.div`
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    background: #f1f5f9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
`;

const EmptyTitle = styled.h3`
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
`;

const EmptyText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #64748b;
`;

const ClientManagement = () => {
    const history = useHistory();
    const [searchBy, setSearchBy] = useState("name");
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");

    const handleSearch = (e) => {
        e?.preventDefault();
        setAppliedSearch(searchTerm.trim());
    };

    const handleClear = () => {
        setSearchTerm("");
        setAppliedSearch("");
    };

    const { data: clientsData, isLoading, isError, error } = useQuery(
        'intermediary-clients',
        async () => {
            const res = await fetchClientsByIntermediary();
            return res.data; // now array of { client, totalPolicies, policies }
        },
        { staleTime: 2 * 60 * 1000 }
    );

    // Deduplicate clients by clientSystemRef / id (in case of overlap)
    const uniqueClients = useMemo(() => {
        if (!Array.isArray(clientsData)) return [];

        const map = new Map();

        clientsData.forEach((entry) => {
            const client = entry.client || {};
            const key = client.clientSystemRef || client.id || client._id;
            if (!key) return;

            if (!map.has(key)) {
                const fullName = [
                    client.names?.firstName,
                    client.names?.middleName,
                    client.names?.lastName
                ].filter(Boolean).join(" ").trim() || "—";

                map.set(key, {
                    id: key,
                    name: fullName,
                    kra_pin: client.kraPin || "—",
                    phone: client.phone || "—",
                    email: client.email || "—",
                    totalPolicies: entry.totalPolicies || 0,
                    clientType: client.clientType || "—",
                    // You can add more aggregates if needed, e.g.:
                    // lastPolicyDate: ...,
                });
            } else {
                // Optional: update totalPolicies if higher, but usually same
                const existing = map.get(key);
                if (entry.totalPolicies > existing.totalPolicies) {
                    existing.totalPolicies = entry.totalPolicies;
                }
            }
        });

        return Array.from(map.values());
    }, [clientsData]);

    const filteredClients = useMemo(() => {
        if (!appliedSearch) return uniqueClients;

        const term = appliedSearch.toLowerCase().trim();

        return uniqueClients.filter((c) => {
            if (searchBy === "name") return c.name.toLowerCase().includes(term);
            if (searchBy === "kra_pin") return c.kra_pin.toLowerCase().includes(term);
            if (searchBy === "phone") return c.phone.toLowerCase().includes(term);
            if (searchBy === "email") return c.email.toLowerCase().includes(term);
            if (searchBy === "id") return c.id.toLowerCase().includes(term);
            return false;
        });
    }, [uniqueClients, appliedSearch, searchBy]);

    const tableData = useMemo(() => {
        return filteredClients.map((c) => ({
            ...c,
            name: c.name,
            kra_pin: c.kra_pin,
            phone: c.phone,
            email: c.email,
            policies: c.totalPolicies,
        }));
    }, [filteredClients]);

    const tableFields = [
        // { name: "ID / Ref" },
        { name: "Name" },
        { name: "KRA PIN" },
        { name: "Phone" },
        { name: "Email" },
        { name: "Policies" },
        { name: "Action" },
    ];

    const viewClient = (clientId) => {
        if (!clientId) return;
        const path = String(routes.viewClient || "/clients/:id").replace(":id", clientId);
        history.push(path);
    };

    const rowData = [
        // { type: "text", field: "id" },
        { type: "text", field: "name" },
        { type: "text", field: "kra_pin" },
        { type: "text", field: "phone" },
        { type: "text", field: "email" },
        { type: "number", field: "policies" },
        {
            type: "action",
            actions: [{ method: viewClient, field: "id", title: "View" }],
        },
    ];

    const getSearchPlaceholder = () => {
        switch (searchBy) {
            case "name": return "Search by client name...";
            case "kra_pin": return "Search by KRA PIN...";
            case "phone": return "Search by phone number...";
            case "email": return "Search by email...";
            case "id": return "Search by client ID...";
            default: return "Search clients...";
        }
    };

    const totalClients = uniqueClients.length;

    return (
        <Page>
            <Header>
                <TitleSection>
                    <Title>Client Management</Title>
                    <Subtitle>
                        View and manage all your clients and their policies
                    </Subtitle>
                </TitleSection>

                <MetricsRow>
                    <MetricCard>
                        <MetricLabel>Total Clients</MetricLabel>
                        <MetricValue>
                            {isLoading ? "—" : isError ? "Error" : totalClients}
                        </MetricValue>
                    </MetricCard>
                    <MetricCard>
                        <MetricLabel>Total Policies</MetricLabel>
                        <MetricValue>
                            {isLoading ? "—" : isError ? "—" : uniqueClients.reduce((sum, c) => sum + c.totalPolicies, 0)}
                        </MetricValue>
                    </MetricCard>
                </MetricsRow>

                <ControlsBar>
                    <SearchSection onSubmit={handleSearch} role="search">
                        <Select
                            value={searchBy}
                            onChange={(e) => setSearchBy(e.target.value)}
                            aria-label="Search by"
                        >
                            <option value="name">Name</option>
                            <option value="kra_pin">KRA PIN</option>
                            <option value="phone">Phone</option>
                            <option value="email">Email</option>
                            <option value="id">ID</option>
                        </Select>

                        <SearchInputWrapper>
                            <Icon.Search size={18} color="#94a3b8" />
                            <SearchInput
                                placeholder={getSearchPlaceholder()}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search clients"
                            />
                        </SearchInputWrapper>

                        <Button type="submit" variant="primary">
                            <Icon.Search size={16} />
                            Search
                        </Button>
                        <Button type="button" onClick={handleClear}>
                            <Icon.X size={16} />
                            Clear
                        </Button>
                    </SearchSection>
                </ControlsBar>
            </Header>

            <ContentCard>
                {isLoading ? (
                    <EmptyState>
                        <EmptyIcon><Icon.Loader size={32} /></EmptyIcon>
                        <EmptyTitle>Loading clients...</EmptyTitle>
                    </EmptyState>
                ) : isError ? (
                    <EmptyState>
                        <EmptyIcon><Icon.AlertTriangle size={32} /></EmptyIcon>
                        <EmptyTitle>Error</EmptyTitle>
                        <EmptyText>{error?.message || "Could not load client data"}</EmptyText>
                    </EmptyState>
                ) : tableData.length === 0 ? (
                    <EmptyState>
                        <EmptyIcon><Icon.Users size={32} /></EmptyIcon>
                        <EmptyTitle>No clients found</EmptyTitle>
                        <EmptyText>
                            {appliedSearch
                                ? "Try different search terms"
                                : "No clients linked to this intermediary yet"}
                        </EmptyText>
                    </EmptyState>
                ) : (
                    <TableComponent
                        title="Clients"
                        table_fields={tableFields}
                        table_data={tableData}
                        row_data={rowData}
                        pageSize={10}
                    />
                )}
            </ContentCard>
        </Page>
    );
};

export default ClientManagement;